import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import TinaAsset from 'components/TinaAsset';
import StyledRichText from 'components/StyledRichText';
import { blockPadV, blockPadH, blockW } from 'theme/mixins/padding';
import type { AnimatedColumnBlock as T } from './types';
import { useInView } from 'framer-motion';
import LottieContainer from './LottieContainer';

const Wrapper = styled.div`
  ${blockPadV()};

  ${({ theme }) => theme.mq.md`
     ${blockPadH()};
  `}

  background: ${({ theme }) => theme.themeColors.bg};
`;

const Container = styled.div`
  ${blockW()};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 3px;

  ${({ theme }) => theme.mq.md`
    flex-direction: row;
    white-space: normal;
    margin: auto;
  `}
`;

const ContentColumn = styled.div`
  background-color: #242323;
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  text-align: center;
  padding: 1.5rem;
  gap: 5.375rem;
  border-radius: 1rem;

  ${({ theme }) => theme.mq.md`
    gap: 4.375rem;
    border-radius: 1.5rem;
  `}

  ${({ theme }) => theme.mq.lg`
    padding: 3.125rem;
    min-width: 18.75rem;
  `}
  
  p {
    font-size: 1.5rem !important;
  }
`;

const styledAsset = css`
  margin-inline: auto;

  ${({ theme }) => theme.mq.ltMd`
  max-width: 22rem; 
  `}
`;

const CenteredAsset = styled.div`
  ${styledAsset}
`;
const ColumnImage = styled(TinaAsset)`
  ${styledAsset}
`;

const AnimatedColumnBlock: React.FC<T> = ({
  firstImage,
  lastImage,
  firstText,
  middleText,
  lastText
}) => {
  const lottieRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(lottieRef, { once: true, amount: 0.5 });
  return (
    <Wrapper>
      <Container>
        <Content>
          <ContentColumn>
            <ColumnImage asset={firstImage} />
            {firstText && <StyledRichText pFormat={firstText?.pFormat} content={firstText?.body} />}
          </ContentColumn>

          <ContentColumn ref={lottieRef}>
            <CenteredAsset>
              <LottieContainer animate={isInView} />
            </CenteredAsset>

            {middleText && (
              <StyledRichText pFormat={middleText?.pFormat} content={middleText?.body} />
            )}
          </ContentColumn>
          <ContentColumn>
            <ColumnImage asset={lastImage} />
            {lastText && <StyledRichText pFormat={lastText?.pFormat} content={lastText?.body} />}
          </ContentColumn>
        </Content>
      </Container>
    </Wrapper>
  );
};

export default AnimatedColumnBlock;
